import { FormLayout } from 'components/common'
import { Table, TableCell, TableRow } from 'components/common/minimalTable'
import { OrderType } from 'types/order'
import { convertTo12HourFormat, formatOrderDate } from 'utils/helpers'
import { getStatus } from './utils'
import { ORDER_STATUS_CLASSES } from 'utils/constants'

const OrderDetailsSection = ({ order }: { order: OrderType }) => {
  const {
    order_no,
    Order_shortcode,
    Store_code,
    order_place_datetime,
    Planned_DeliveryDate,
    Delivery_provider,
    order_value_inclv_tax,
    service_fee,
    delivery_fee,
    customer_Name,
    customer_Address,
    customer_Address2,
    Area,
    City,
    Contact,
    products = [],
    payment = [],
    Country,
    invoiceNo,
    storeName,
    Aggregator_Code,
    Aggregator_Name,
    lastMile,
    totalAmountExclVat,
    totalVat,
    createdAt,
    status,
  } = order

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="title">Order Details</h3>
        <p className="rounded-lg bg-gray-800 px-3 py-1 text-sm font-semibold text-white shadow">
          {order_no}
        </p>
      </div>

      <FormLayout className="!p-4 text-sm">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <p>
              <span className="font-bold">Invoice No:</span>{' '}
              {invoiceNo ?? 'N/A'}
            </p>
            <p>
              <span className="font-bold">Order Shortcode:</span>{' '}
              {Order_shortcode ?? 'N/A'}
            </p>
            <p>
              <span className="font-bold">Store Name:</span>{' '}
              {storeName ?? 'N/A'}
            </p>
            <p>
              <span className="font-bold">Store Code:</span>{' '}
              {Store_code ?? 'N/A'}
            </p>
            <p>
              <span className="font-bold">Aggregator Name:</span>{' '}
              {Aggregator_Name ?? 'N/A'}
            </p>
            <p>
              <span className="font-bold">Aggregator Code:</span>{' '}
              {Aggregator_Code ?? 'N/A'}
            </p>
            <p>
              <span className="font-bold">Order Date:</span>{' '}
              {order_place_datetime
                ? formatOrderDate(order_place_datetime)
                : 'N/A'}
            </p>
            <p>
              <span className="font-bold">Planned Delivery:</span>{' '}
              {Planned_DeliveryDate
                ? formatOrderDate(Planned_DeliveryDate)
                : 'N/A'}
            </p>
            <p>
              <span className="font-bold">Order Posting Date:</span>{' '}
              {createdAt ? convertTo12HourFormat(createdAt) : 'N/A'}
            </p>
            <p>
              <span className="font-bold">Status :</span>{' '}
              <span
                className={`rounded-lg px-2 py-1 text-sm font-semibold text-black ${
                  ORDER_STATUS_CLASSES[status!]
                }`}
              >
                {status}
              </span>
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <p>
              <span className="font-bold">Order Value (Incl. Tax):</span>{' '}
              {order_value_inclv_tax?.toFixed(2) ?? 'N/A'}
            </p>
            <p>
              <span className="font-bold">Order Value (Excl. Tax):</span>{' '}
              {totalAmountExclVat?.toFixed(2) ?? 'N/A'}
            </p>
            <p>
              <span className="font-bold">VAT Amount:</span>{' '}
              {totalVat?.toFixed(2) ?? 'NA'}
            </p>
            <p>
              <span className="font-bold">Service Fee:</span>{' '}
              {service_fee ?? 'N/A'}
            </p>
            <p>
              <span className="font-bold">Delivery Fee:</span>{' '}
              {delivery_fee ?? 'N/A'}
            </p>

            <p>
              <span className="font-bold">Country:</span> {Country ?? 'N/A'}
            </p>
            <p>
              <span className="font-bold">Delivery Provider:</span>{' '}
              {Delivery_provider ?? 'N/A'}
            </p>
            <p>
              <span className="font-bold">Last Mile :</span> {lastMile ?? 'N/A'}
            </p>
            <p>
              <span className="font-bold">Last Mile Sync :</span>{' '}
              <span
                className={`rounded-lg px-2 py-0.5 text-sm font-semibold ${
                  order?.lastMileSync
                    ? 'bg-green-400 text-black'
                    : 'bg-red-500  text-white'
                }`}
              >
                {getStatus(order?.lastMileSync)}
              </span>
            </p>
            <p>
              <span className="font-bold">Erp Sync :</span>{' '}
              <span
                className={`rounded-lg px-2 py-0.5 text-sm font-semibold ${
                  order?.erpSync
                    ? 'bg-green-400 text-black'
                    : 'bg-red-500  text-white'
                }`}
              >
                {getStatus(order?.erpSync)}
              </span>
            </p>
          </div>
        </div>
      </FormLayout>

      <h3 className="title mt-3">Customer Details</h3>
      <FormLayout className="grid !p-4 text-sm sm:grid-cols-2">
        <>
          <div className="flex flex-col gap-1.5">
            <p>
              <span className="font-bold">Name:</span> {customer_Name ?? 'N/A'}
            </p>
            <p>
              <span className="font-bold">Address:</span>{' '}
              {customer_Address ?? 'N/A'}
            </p>
            <p>
              <span className="font-bold">Address 2:</span>{' '}
              {customer_Address2 ?? 'N/A'}
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <p>
              <span className="font-bold">Area:</span> {Area ?? 'N/A'}
            </p>
            <p>
              <span className="font-bold">City:</span> {City ?? 'N/A'}
            </p>
            <p>
              <span className="font-bold">Contact:</span> {Contact ?? 'N/A'}
            </p>
          </div>
        </>
      </FormLayout>

      <h3 className="title mt-3">Products</h3>
      {products?.length > 0 ? (
        <Table
          headers={[
            'Barcode',
            'Quantity',
            'Grandiose Price',
            'Unit Price (Incl. Tax)',
            'Unit Price (Excl. Tax)',
            'Unit VAT',
            'Total VAT',
            'Total',
          ]}
        >
          {products.map((product) => (
            <TableRow key={product._id}>
              <TableCell>
                {product?.description && (
                  <p className="mb-1 font-bold">{product.description}</p>
                )}
                <p>
                  <strong>SKU:</strong> {product.barcode}
                </p>
                <p>
                  <strong>UOM:</strong> {product?.uom || 'NA'}
                </p>
              </TableCell>
              <TableCell>{product.qty}</TableCell>
              <TableCell>{product?.systemPrice?.toFixed(2) ?? 'NA'}</TableCell>
              <TableCell>
                {product?.unit_price_inclv_tax?.toFixed(2) ?? 'NA'}
              </TableCell>
              <TableCell>
                {product?.amountExclVat?.toFixed(2) ?? 'NA'}
              </TableCell>
              <TableCell>
                {product?.unitVat !== undefined
                  ? product.unitVat?.toFixed(2)
                  : 'NA'}
              </TableCell>
              <TableCell>
                {product?.unitVat !== undefined
                  ? (product?.unitVat * product.qty).toFixed(2)
                  : 'NA'}
              </TableCell>

              <TableCell>
                {(Number(product?.unit_price_inclv_tax) * product?.qty).toFixed(
                  2,
                )}
              </TableCell>
            </TableRow>
          ))}
        </Table>
      ) : (
        <p className="text-sm text-gray-600">No Product details available</p>
      )}

      <h3 className="title mt-3">Payments</h3>
      {payment?.length > 0 ? (
        <Table headers={['Payment Type', 'Amount']}>
          {payment.map((pay) => (
            <TableRow key={pay._id}>
              <TableCell>{pay.payment_type}</TableCell>
              <TableCell>{pay.amount?.toFixed(2)}</TableCell>
            </TableRow>
          ))}
        </Table>
      ) : (
        <p className="text-sm text-gray-600">No Payment details available</p>
      )}
    </>
  )
}

export default OrderDetailsSection
